import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { X, ShieldCheck } from 'lucide-react'
import { useLanguage } from './LanguageContext'

type PaymentOverlayValue = {
  open: (url: string) => void
  close: () => void
}

const PaymentOverlayContext = createContext<PaymentOverlayValue | null>(null)

export const usePaymentOverlay = () => {
  const ctx = useContext(PaymentOverlayContext)
  if (!ctx) {
    throw new Error('usePaymentOverlay must be used within a PaymentOverlayProvider')
  }
  return ctx
}

export const PaymentOverlayProvider = ({ children }: { children: any }) => {
  const [url, setUrl] = useState<string | null>(null)
  const { language } = useLanguage()

  const open = useCallback((u: string) => setUrl(u), [])
  const close = useCallback(() => setUrl(null), [])

  // The in-iframe /payment-complete page asks us to close via postMessage.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e?.data === 'storyfy:payment-close') setUrl(null)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  // Esc closes the overlay.
  useEffect(() => {
    if (!url) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUrl(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [url])

  return (
    <PaymentOverlayContext.Provider value={{ open, close }}>
      {children}
      {url && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center sm:p-4">
          <div
            className="absolute inset-0 bg-celebration-ink/70 backdrop-blur-sm"
            onClick={close}
            data-testid="payment-overlay-backdrop"
          />
          <div className="relative flex w-full flex-col overflow-hidden bg-white shadow-celebrate-lg dark:bg-[#1e1438] h-[100dvh] sm:h-[88vh] sm:max-h-[760px] sm:w-full sm:max-w-md sm:rounded-bubble">
            <div className="flex h-12 shrink-0 items-center justify-between border-b border-gray-100 px-4 dark:border-white/10">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-600 dark:text-gray-300">
                <ShieldCheck className="h-4 w-4 text-celebration-pink" />
                {language === 'ka' ? 'უსაფრთხო გადახდა' : 'Secure payment'}
              </div>
              <button
                type="button"
                onClick={close}
                aria-label={language === 'ka' ? 'დახურვა' : 'Close'}
                data-testid="payment-overlay-close"
                className="text-gray-400 transition-colors hover:text-gray-700 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <iframe
              title="Payment"
              src={url}
              className="w-full flex-1 border-0"
              allow="payment *"
            />
          </div>
        </div>
      )}
    </PaymentOverlayContext.Provider>
  )
}
